/**
 * @fileoverview Hook UI Layer para gestión visual y handlers del header de la ventana de chat en Inbox.
 * @module useHeaderChatWindowUI
 * @category Hook - UI Layer
 * @since 1.0.0
 * @version 3.1.0
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import STATUS_CHAT_MAP from '../../../utils/statusChatMap';
import { getCommonAllowedStateIds, isStateOptionDisabled } from '../../../utils/chatStateOptions';
import useHeaderChatWindowActions from '../actions/useHeaderChatWindowActions';
import { fetchInboxAgentsByRoleIfEmpty } from '../../../../actions/inbox/retailerUserActions';
import useSelectedConversation from '../../shared/useSelectedConversation';
import {
  isInboxMsChat,
  toSidebarPlatformKey
} from '../../../../services/inbox/inboxMsChatPlatform';
import { isWhatsappCallsConnectedForRetailer } from '../../calls/resolveCallProvider';

// Hook de lógica de UI para HeaderChatWindowMC
/**
 * Hook UI Layer para gestión visual y handlers del header de la ventana de chat en Inbox.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades:
 * - Solo maneja estado visual, opciones de menú, labels y handlers de UI.
 * - No contiene lógica de negocio ni llamadas a API.
 * - Optimizado con useMemo, useCallback y debug tracking.
 *
 * @function useHeaderChatWindowUI
 * @returns {Object} Estado visual, opciones y handlers para el header del chat
 * @see useHeaderChatWindowActions
 * @since 1.0.0
 * @version 3.1.0 - Optimización y debug tracking
 */
export default function useHeaderChatWindowUI() {
  const dispatch = useDispatch();

  const { t } = useTranslation('InboxMC');
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser || {});
  const agentList = useSelector((state) => state.inboxAgentsReducer.inboxAgentList || []);
  const agentsLoading = useSelector((state) => state.inboxAgentsReducer.loading || false);
  const agentsError = useSelector((state) => state.inboxAgentsReducer.error || null);
  const conversation = useSelectedConversation();
  const inboxCustomer = useSelector((state) => state.inboxRightReducer.customer);
  const { retailer_info } = useSelector((state) => state.retailerUsersReducer);
  const canAssignChatsToAgents = Boolean(currentRetailerUser?.can_assign_chats_to_agents);
  const canToggleCustomerBlock = Boolean(currentRetailerUser?.can_toggle_customer_block);
  const canMakeCalls = currentRetailerUser?.can_make_calls ?? true;

  useEffect(() => {
    dispatch(fetchInboxAgentsByRoleIfEmpty());
  }, [dispatch]);

  // Importa handlers de negocio desde el hook de actions
  const {
    handleMessagingState,
    handleChangeAgent,
    handleToggleBlockUser,
    handleToggleChatFlow,
    handleUnreadChat
  } = useHeaderChatWindowActions(conversation);

  // Función memoizada para construir opciones de estado de chat
  const buildStateOptions = useCallback(
    (keys) =>
      keys.map((key) => {
        const state = STATUS_CHAT_MAP[Number(key)];
        return {
          id: Number(key),
          label: t(`header.state_${state.value}`),
          onClick: () => handleMessagingState(Number(key)),
          disabled: conversation?.statusChat === Number(key)
        };
      }),
    [conversation?.statusChat, handleMessagingState, t]
  );

  // Obtener los estados dinámicamente usando Object.keys
  const messagingStatesOptionsMap = useMemo(
    () => buildStateOptions(Object.keys(STATUS_CHAT_MAP)),
    [buildStateOptions]
  );

  const currentStatusValues = useMemo(() => {
    if (conversation?.statusChat === undefined || conversation?.statusChat === null) return [];
    return [Number(conversation.statusChat)];
  }, [conversation?.statusChat]);

  // Genera las opciones de estado de chat según el/los estado(s) actual(es)
  const chatStateOptions = useMemo(() => {
    if (!currentStatusValues.length) return [];

    const commonAllowedStateIds = getCommonAllowedStateIds(currentStatusValues);

    return commonAllowedStateIds
      .map((stateId) => messagingStatesOptionsMap.find((option) => option.id === stateId))
      .filter(Boolean)
      .map((option) => ({
        ...option,
        disabled: isStateOptionDisabled(currentStatusValues, option.id)
      }));
  }, [currentStatusValues, messagingStatesOptionsMap]);

  // Devuelve el label traducido del estado del chat
  const statusChatLabel = useMemo(() => {
    const found = messagingStatesOptionsMap.find(
      (option) => option.id === conversation?.statusChat
    );
    return found ? found.label : '';
  }, [messagingStatesOptionsMap, conversation?.statusChat]);

  // Memoizar serialización de agentes para evitar recálculos innecesarios
  const agentListOptions = useMemo(() => {
    if (!Array.isArray(agentList)) return [];
    return [
      { id: 'assign_agent', value: '', label: t('header.assign_agent') },
      ...agentList.map((agent) => {
        const fullName =
          agent.first_name && agent.last_name
            ? `${agent.first_name} ${agent.last_name}`
            : agent.email;
        return {
          id: agent.id,
          value: agent.id,
          label: fullName
        };
      })
    ];
  }, [agentList, canAssignChatsToAgents, t]);

  const blockUserTooltiptext = useMemo(() => {
    if (!canToggleCustomerBlock) {
      return t('header.block_permission_blocked_notice', {
        defaultValue: 'No tienes permiso para bloquear o desbloquear usuarios'
      });
    }

    return conversation?.userIsBlocked ? t('header.unblock_user') : t('header.block_user');
  }, [canToggleCustomerBlock, conversation?.userIsBlocked, t]);

  const flowTooltiptext = useMemo(
    () => (conversation.hasAllowFlow ? t('header.deactivate_flow') : t('header.activate_flow')),
    [conversation.hasAllowFlow, t]
  );

  /**
   * Cubre las tres variantes del inbox MS (BSP / QR / Gupshup) además del `whatsapp`
   * del monolito. Comparar el string pelado dejaba a los chats del MS sin estos botones.
   */
  const isWhatsappChat = useMemo(
    () => toSidebarPlatformKey(conversation?.platform) === 'whatsapp',
    [conversation?.platform]
  );

  const showButtonBlockUser = useMemo(() => isWhatsappChat, [isWhatsappChat]);
  const showButtonFlow = useMemo(
    () =>
      conversation?.platform === 'whatsapp' ||
      conversation?.platform === 'messenger' ||
      conversation?.platform === 'instagram' ||
      isInboxMsChat(conversation?.platform),
    [conversation]
  );
  const showButtonFonemaCall = useMemo(
    () => isWhatsappChat
      && isWhatsappCallsConnectedForRetailer(retailer_info)
      && canMakeCalls,
    [isWhatsappChat, retailer_info, canMakeCalls]
  );
  const fonemaCallCustomer = useMemo(() => {
    if (!inboxCustomer || !conversation?.customerId) return null;
    if (Number(inboxCustomer.id) !== Number(conversation.customerId)) return null;
    return inboxCustomer;
  }, [inboxCustomer, conversation?.customerId]);
  const selectedAgent = useMemo(
    () => conversation?.assignedAgent?.id || 'assign_agent',
    [conversation?.assignedAgent?.id]
  );
  const showAssignToSelfButton = useMemo(
    () => !canAssignChatsToAgents && conversation?.assignedAgent?.id !== currentRetailerUser?.id,
    [canAssignChatsToAgents, conversation?.assignedAgent?.id, currentRetailerUser?.id]
  );
  const handleAssignToSelf = useCallback(() => {
    if (currentRetailerUser?.id) {
      handleChangeAgent(currentRetailerUser.id);
    }
  }, [currentRetailerUser?.id, handleChangeAgent]);

  return {
    showButtonBlockUser,
    showButtonFlow,
    showButtonFonemaCall,
    fonemaCallCustomer,
    agentListOptions,
    chatStateOptions,
    agentsLoading,
    agentsError,
    canAssignChatsToAgents,
    canToggleCustomerBlock,
    showAssignToSelfButton,
    handleAssignToSelf,
    selectedAgent,
    statusChatLabel,
    fullNameCustomer: conversation.fullName,
    blockUserTooltiptext,
    userIsBlocked: conversation.userIsBlocked,
    hasAllowFlow: conversation.hasAllowFlow,
    isUnRead: conversation.isUnRead,
    flowTooltiptext,
    handleToggleBlockUser,
    handleUnreadChat,
    handleToggleChatFlow,
    handleChangeAgent
  };
}
